// Task #3972 — Deep-link resolution for the unified Team Inbox.
//
// The assignment-notification dispatcher links recipients to
// `/app/dashboard?inbox=<kind>:<id>`, where <kind> is the *source* entity kind
// (`task` | `todo` | `member_message`) — NOT the Team Inbox item's internal
// `kind`. This module maps that param onto the concrete Team Inbox item key so
// the dashboard can scroll to + flash the matching row.
//
// Item-key mapping (mirrors the server team-inbox builder in storage.ts):
//   - todo:<id>            → item.kind === "todo"           && item.id === id
//   - member_message:<id>  → item.kind === "member_message" && item.id === id
//   - task:<id>            → item.kind === "event" whose link points at
//                            `?task=<id>` (prefer eventType "maintenance")

export type InboxTargetKind = "task" | "todo" | "member_message" | "event";

export interface InboxTarget {
  kind: InboxTargetKind;
  id: number;
}

// Minimal structural shape the resolver needs. The real `TeamInboxItem` in
// app-dashboard.tsx is a structural superset of this.
export interface InboxItemLike {
  key: string;
  kind: "member_message" | "event" | "todo";
  id: number;
  eventType?: string | null;
  link?: string | null;
}

const TARGET_KINDS: ReadonlySet<string> = new Set<InboxTargetKind>([
  "event",
  "task",
  "todo",
  "member_message",
]);

// Parse the `?inbox=<kind>:<id>` query param. Accepts the raw search string with
// or without a leading "?". Returns null for anything malformed so callers can
// no-op silently.
export function parseInboxParam(search: string | null | undefined): InboxTarget | null {
  if (!search) return null;
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  let raw: string | null;
  try {
    raw = new URLSearchParams(normalized).get("inbox");
  } catch {
    return null;
  }
  if (!raw) return null;
  const sep = raw.indexOf(":");
  if (sep <= 0) return null;
  const kind = raw.slice(0, sep);
  const idStr = raw.slice(sep + 1);
  if (!TARGET_KINDS.has(kind)) return null;
  if (!/^\d+$/.test(idStr)) return null;
  const id = Number(idStr);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  return { kind: kind as InboxTargetKind, id };
}

// Extract the numeric `task` id from a maintenance deep link
// (`/app/maintenance?task=123`). Returns null when absent/non-numeric.
function taskIdFromLink(link: string | null | undefined): number | null {
  if (!link) return null;
  const m = /[?&]task=(\d+)/.exec(link);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// Resolve a parsed target onto a concrete Team Inbox item key, or null when no
// currently-loaded item matches (the target row may not be in the page yet).
export function resolveInboxTargetKey(
  items: readonly InboxItemLike[],
  target: InboxTarget | null,
): string | null {
  if (!target) return null;
  if (target.kind === "todo") {
    const hit = items.find((i) => i.kind === "todo" && i.id === target.id);
    return hit ? hit.key : null;
  }
  if (target.kind === "member_message") {
    const hit = items.find((i) => i.kind === "member_message" && i.id === target.id);
    return hit ? hit.key : null;
  }
  if (target.kind === "event") {
    const hit = items.find((i) => i.kind === "event" && i.id === target.id);
    return hit ? hit.key : null;
  }
  // task → event row whose link references this task id. Prefer the canonical
  // maintenance event when several events share the same task link.
  const candidates = items.filter(
    (i) => i.kind === "event" && taskIdFromLink(i.link) === target.id,
  );
  if (candidates.length === 0) return null;
  const preferred = candidates.find((i) => i.eventType === "maintenance");
  return (preferred ?? candidates[0]).key;
}
